import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { InductionPerson, InductionRegister } from '../../types/induction';
import { formatDateInTz } from '../../utils/timezone';
import { packApplies, personLabel } from './inductionLabels';

interface Props {
  register: InductionRegister;
  people: InductionPerson[];
  tz: string;
}

/** Who has acknowledged which induction-pack policy. A wide table scrolls sideways. */
export default function PackMatrix({ register, people, tz }: Props) {
  if (!register.pack.length) {
    return <p className="text-sm text-neutral-500">No policies are in the induction pack yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-200">
      <table className="min-w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs font-medium text-neutral-600">
          <tr>
            <th className="px-3 py-2 min-w-[10rem]">Person</th>
            {register.pack.map(d => (
              <th key={d.id} className="px-3 py-2 min-w-[7rem] max-w-[11rem] align-bottom break-words">{d.title}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {people.map(p => (
            <tr key={p.ref}>
              <td className="px-3 py-2 max-w-[14rem]">
                <Link to={`/inductions/people/${p.ref}`} className="font-medium text-primary-700 hover:underline break-words">
                  {personLabel(p.name)}
                </Link>
              </td>
              {register.pack.map(d => {
                if (!packApplies(d.audience, p.worker_type)) {
                  return <td key={d.id} className="px-3 py-2 text-neutral-300">n/a</td>;
                }
                const cell = p.worker_id ? register.pack_cells[`w${p.worker_id}:${d.id}`] : undefined;
                return (
                  <td key={d.id} className="px-3 py-2 whitespace-nowrap">
                    {!cell && <span className="text-error-700">Due</span>}
                    {cell?.status === 'updated' && <span className="text-warning-800">Updated — due</span>}
                    {cell?.status === 'acknowledged' && (
                      <span className="inline-flex items-center gap-1 text-success-700">
                        <Check className="h-4 w-4" />{formatDateInTz(cell.acknowledged_at, tz, { day: 'numeric', month: 'short', year: '2-digit' })}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
